import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Eraser, Save, Check, Eye, EyeOff, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

/**
 * Canvas HTML5 de dessin libre par-dessus un schéma SVG en fond.
 * - Pointeur unifié (souris + tactile)
 * - Couleur primaire `#cf2031`
 * - Auto-save dataURL en localStorage à chaque relâchement
 * - Restauration automatique au montage
 *
 * Props :
 *   storageKey      : clé localStorage
 *   backgroundSvg   : ReactNode (schéma de la situation)
 *   idealOverlay    : ReactNode (trajectoire idéale en vert) — affichée quand
 *                     l'élève clique sur "Voir la correction"
 *   height          : hauteur en px (défaut 360)
 *   testid          : test-id de base
 *   ariaLabel       : label a11y
 */
export function TrajectoryCanvas({
  storageKey,
  backgroundSvg,
  idealOverlay,
  ariaLabel = 'Zone de dessin',
  testid = 'trajectory-canvas',
  height = 360,
}) {
  const canvasRef = useRef(null);
  const wrapperRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef(null);
  const [hasContent, setHasContent] = useState(false);
  const [validated, setValidated] = useState(false);
  const [showIdeal, setShowIdeal] = useState(false);
  const [overlayKey, setOverlayKey] = useState(0); // force re-mount pour relancer l'animation draw-in

  const getCtx = () => {
    const c = canvasRef.current;
    if (!c) return null;
    return c.getContext('2d');
  };

  // Resize canvas to match wrapper width while keeping aspect
  const fitCanvas = useCallback(() => {
    const c = canvasRef.current;
    const w = wrapperRef.current;
    if (!c || !w) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = w.getBoundingClientRect();
    const cssWidth = rect.width;
    const cssHeight = height;
    c.style.width = cssWidth + 'px';
    c.style.height = cssHeight + 'px';
    c.width = Math.round(cssWidth * dpr);
    c.height = Math.round(cssHeight * dpr);
    const ctx = c.getContext('2d');
    if (ctx) {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.lineWidth = 4;
      ctx.strokeStyle = '#cf2031';
    }
  }, [height]);

  // Restore saved drawing
  const restore = useCallback(() => {
    if (!storageKey) return;
    try {
      const saved = localStorage.getItem(storageKey);
      if (!saved) { setHasContent(false); return; }
      const img = new Image();
      img.onload = () => {
        const c = canvasRef.current;
        if (!c) return;
        const ctx = c.getContext('2d');
        if (!ctx) return;
        const dpr = window.devicePixelRatio || 1;
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, c.width, c.height);
        ctx.drawImage(img, 0, 0, c.width, c.height);
        ctx.restore();
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        setHasContent(true);
        // Si l'élève avait déjà sauvegardé, on considère le dessin "validé"
        try {
          const v = localStorage.getItem(storageKey + ':validated');
          if (v === '1') setValidated(true);
        } catch { /* ignore */ }
      };
      img.src = saved;
    } catch { /* ignore */ }
  }, [storageKey]);

  useEffect(() => {
    fitCanvas();
    restore();
    const onResize = () => { fitCanvas(); restore(); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [fitCanvas, restore]);

  const getPointerPos = (e) => {
    const c = canvasRef.current;
    if (!c) return null;
    const rect = c.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const handlePointerDown = (e) => {
    e.preventDefault();
    const pt = getPointerPos(e);
    if (!pt) return;
    drawingRef.current = true;
    lastPointRef.current = pt;
    const ctx = getCtx();
    if (!ctx) return;
    ctx.beginPath();
    ctx.moveTo(pt.x, pt.y);
    ctx.lineTo(pt.x + 0.01, pt.y + 0.01);
    ctx.stroke();
    setHasContent(true);
    // Toute nouvelle modification annule la "validation"
    if (validated) {
      setValidated(false);
      try { localStorage.removeItem(storageKey + ':validated'); } catch { /* ignore */ }
    }
  };

  const handlePointerMove = (e) => {
    if (!drawingRef.current) return;
    const pt = getPointerPos(e);
    if (!pt) return;
    const ctx = getCtx();
    const last = lastPointRef.current;
    if (!ctx || !last) return;
    ctx.beginPath();
    ctx.moveTo(last.x, last.y);
    ctx.lineTo(pt.x, pt.y);
    ctx.stroke();
    lastPointRef.current = pt;
  };

  const persist = useCallback(() => {
    if (!storageKey) return;
    try {
      const c = canvasRef.current;
      if (!c) return;
      const url = c.toDataURL('image/png');
      localStorage.setItem(storageKey, url);
    } catch (err) {
      // localStorage quota exceeded — silent fallback
      console.warn('[TrajectoryCanvas] persist failed', err);
    }
  }, [storageKey]);

  const handlePointerUp = () => {
    if (drawingRef.current) {
      drawingRef.current = false;
      lastPointRef.current = null;
      persist();
    }
  };

  const erase = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.restore();
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    if (storageKey) {
      try {
        localStorage.removeItem(storageKey);
        localStorage.removeItem(storageKey + ':validated');
      } catch { /* ignore */ }
    }
    setHasContent(false);
    setValidated(false);
    setShowIdeal(false);
  };

  const save = () => {
    persist();
    setValidated(true);
    try { localStorage.setItem(storageKey + ':validated', '1'); } catch { /* ignore */ }
    toast.success('Trajectoire validée — tu peux maintenant comparer avec la correction', {
      duration: 3500,
    });
  };

  const toggleIdeal = () => {
    setShowIdeal((v) => {
      const next = !v;
      if (next) setOverlayKey((k) => k + 1); // relance l'animation draw-in
      return next;
    });
  };

  const canShowIdeal = !!idealOverlay && validated;

  return (
    <div className="space-y-2">
      <div
        ref={wrapperRef}
        className="relative rounded-xl border border-border overflow-hidden bg-[hsl(var(--panel))]"
        style={{ height: `${height}px` }}
        data-testid={testid}
      >
        {/* Schéma de fond (asphalte + points) */}
        {backgroundSvg ? (
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center" aria-hidden="true">
            {backgroundSvg}
          </div>
        ) : null}

        {/* Trajectoire idéale (overlay vert) — au-dessus du schéma, en-dessous du canvas */}
        {showIdeal && idealOverlay ? (
          <div
            key={overlayKey}
            className="absolute inset-0 pointer-events-none flex items-center justify-center c2r-ideal-overlay"
            data-testid={`${testid}-ideal`}
            aria-hidden="true"
          >
            {idealOverlay}
          </div>
        ) : null}

        <canvas
          ref={canvasRef}
          className="absolute inset-0 touch-none cursor-crosshair"
          style={{ touchAction: 'none' }}
          aria-label={ariaLabel}
          role="img"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
          onPointerCancel={handlePointerUp}
          data-testid={`${testid}-canvas`}
        />

        {/* Badge "Trajectoire idéale" en haut à gauche quand affichée */}
        {showIdeal && idealOverlay ? (
          <div
            className="absolute top-2 left-2 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-widest text-white shadow-lg c2r-badge-pop"
            style={{ background: '#16a34a', zIndex: 4 }}
          >
            <Sparkles className="w-3 h-3" />
            Trajectoire idéale
          </div>
        ) : null}
      </div>

      <div className="flex items-center gap-2 justify-end flex-wrap">
        <Button
          variant="outline"
          size="sm"
          onClick={erase}
          disabled={!hasContent}
          className="rounded-full"
          data-testid={`${testid}-erase`}
        >
          <Eraser className="w-3.5 h-3.5 mr-1.5" /> Effacer
        </Button>

        <Button
          size="sm"
          onClick={save}
          disabled={!hasContent}
          className="mc-btn-primary rounded-full"
          data-testid={`${testid}-save`}
        >
          {validated ? <Check className="w-3.5 h-3.5 mr-1.5" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
          {validated ? 'Validée' : 'Valider mon tracé'}
        </Button>

        {idealOverlay ? (
          <Button
            size="sm"
            onClick={toggleIdeal}
            disabled={!canShowIdeal}
            className={`rounded-full font-black ${canShowIdeal && !showIdeal ? 'c2r-pulse-soft' : ''}`}
            style={{
              background: showIdeal ? '#15803d' : '#16a34a',
              color: 'white',
              opacity: canShowIdeal ? 1 : 0.5,
            }}
            data-testid={`${testid}-show-ideal`}
            title={!canShowIdeal ? 'Valide ton tracé pour révéler la correction' : ''}
          >
            {showIdeal
              ? (<><EyeOff className="w-3.5 h-3.5 mr-1.5" /> Cacher la correction</>)
              : (<><Eye className="w-3.5 h-3.5 mr-1.5" /> Voir la correction</>)}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
